// Generates price ranges based on available spaces
export const generatePriceRanges = (spaces) => {
    if (!spaces || spaces.length === 0) {
        return [
            { label: 'LKR 1000 - 2500', value: '1000-2500' },
            { label: 'LKR 2500 - 5000', value: '2500-5000' },
            { label: 'LKR 5000+', value: '5000+' }
        ];
    }

    // Extract all prices from spaces
    const prices = spaces
        .map(space => parseFloat(space.rate))
        .filter(price => !isNaN(price) && price > 0);

    if (prices.length === 0) {
        return [
            { label: 'LKR 1000 - 2500', value: '1000-2500' },
            { label: 'LKR 2500 - 5000', value: '2500-5000' },
            { label: 'LKR 5000+', value: '5000+' }
        ];
    }

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // Generate dynamic price ranges based on actual data
    const ranges = [];

    // Only create ranges based on actual price data
    if (minPrice <= 2500) {
        const upperBound = Math.min(2500, maxPrice);
        ranges.push({ label: `LKR ${minPrice} - ${upperBound}`, value: `${minPrice}-${upperBound}` });
    }

    if (maxPrice > 2500 && minPrice <= 5000) {
        const lowerBound = Math.max(2500, minPrice);
        const upperBound = Math.min(5000, maxPrice);
        if (lowerBound < upperBound) {
            ranges.push({ label: `LKR ${lowerBound} - ${upperBound}`, value: `${lowerBound}-${upperBound}` });
        }
    }

    if (maxPrice > 5000) {
        const lowerBound = Math.max(5000, minPrice);
        ranges.push({ label: `LKR ${lowerBound}+`, value: `${lowerBound}+` });
    }

    // If no ranges were created from actual data, use fallback
    return ranges.length > 0 ? ranges : [
        { label: 'LKR 1000 - 2500', value: '1000-2500' },
        { label: 'LKR 2500 - 5000', value: '2500-5000' },
        { label: 'LKR 5000+', value: '5000+' }
    ];
};

// Filters spaces based on selected location
export const filterSpacesByLocation = (spaces, location) => {
    if (!spaces || !location) return [];

    return spaces.filter(space => {
        const spaceLocation = space.location?.city || space.location?.address || '';
        return spaceLocation.toLowerCase().includes(location.toLowerCase());
    });
};

// Filters spaces based on price range
export const filterSpacesByPriceRange = (spaces, priceRange) => {
    if (!spaces || !priceRange) return [];

    const [minPrice, maxPrice] = priceRange.value.split('-').map(p => parseFloat(p));

    return spaces.filter(space => {
        const spacePrice = parseFloat(space.rate);
        if (isNaN(spacePrice) || spacePrice <= 0) {
            return false; // Skip spaces with invalid prices
        }

        if (maxPrice === Infinity || isNaN(maxPrice)) {
            return spacePrice >= minPrice; // For "5000+" range
        }
        return spacePrice >= minPrice && spacePrice <= maxPrice;
    });
};

// Builds context object for AI responses
export const buildGeminiContext = (contextData) => {
    const {
        userName,
        bookingCount,
        isLoggedIn,
        selectedWorkspaceType,
        availableWorkspaceTypes,
        availableSpaces,
        conversationHistory,
        currentStep,
        selectedSpace
    } = contextData;

    const context = {
        userInfo: {
            name: userName,
            bookingCount: bookingCount,
            isLoggedIn: isLoggedIn
        },
        selectedWorkspaceType: selectedWorkspaceType,
        availableWorkspaceTypes: availableWorkspaceTypes,
        availableSpaces: availableSpaces,
        conversationHistory: conversationHistory.map(msg => ({
            sender: msg.sender,
            text: msg.text,
            timestamp: msg.timestamp
        })),
        currentStep: currentStep,
        selectedSpace: selectedSpace
    };

    return context;
};

// Processes and formats workspace options from API response
export const processWorkspaceOptions = (activitiesData, eventTypeData) => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
        return [];
    }

    const eventTypeMap = {};
    if (eventTypeData && eventTypeData.eventTypes) {
        eventTypeData.eventTypes.forEach(eventType => {
            eventTypeMap[eventType.code] = eventType.id;
        });
    }

    const options = [];
    activitiesData.forEach(category => {
        if (category.venueType && Array.isArray(category.venueType)) {
            category.venueType.forEach(venue => {
                options.push({
                    label: venue.name,
                    value: venue.name,
                    code: venue.code,
                    eventTypeId: eventTypeMap[venue.code],
                    description: venue.description,
                    category: category.name,
                    rateUnit: venue.rateUnit,
                    rate: venue.rate
                });
            });
        }
    });

    return options;
};

// Extracts and groups locations from spaces data
export const extractLocationOptions = (spaces) => {
    if (!spaces || !Array.isArray(spaces)) {
        return { locationOptions: [], locationGroups: {} };
    }

    // Extract locations from spaces
    const locations = [...new Set(spaces.map(space => {
        const location = space.location;
        return typeof location === 'object' ? location.city : location;
    }).filter(loc => loc && loc !== 'N/A' && loc.trim() !== ''))];

    if (locations.length === 0) {
        return { locationOptions: [], locationGroups: {} };
    }

    // Group locations by base name (e.g., "Colombo 01", "Colombo 02" -> "Colombo")
    const locationGroups = {};
    locations.forEach(location => {
        // Extract base name (remove numbers and extra spaces)
        let baseName = location.replace(/\s+\d+$/, '').replace(/\s+$/, '').trim();
        baseName = baseName.toLowerCase();

        if (!locationGroups[baseName]) {
            locationGroups[baseName] = [];
        }
        if (!locationGroups[baseName].includes(location)) {
            locationGroups[baseName].push(location);
        }
    });

    const locationOptions = Object.keys(locationGroups).map(baseName => ({
        label: baseName.split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' '),
        value: baseName
    }));

    return { locationOptions, locationGroups };
};

// Extracts rate options from spaces data
export const extractRateOptions = (spaces) => {
    if (!spaces || !Array.isArray(spaces)) {
        return [];
    }

    const rateUnits = [...new Set(spaces.map(space => space.rateUnit))];
    return rateUnits.map(unit => ({
        label: `Per ${unit.charAt(0).toUpperCase() + unit.slice(1)}`,
        value: unit
    }));
};

// Validates and sanitizes user input
export const validateUserInput = (input) => {
    if (!input || typeof input !== 'string') {
        return { isValid: false, sanitized: '', error: 'Invalid input' };
    }

    const sanitized = input.trim();

    if (sanitized.length === 0) {
        return { isValid: false, sanitized: '', error: 'Input cannot be empty' };
    }

    if (sanitized.length > 1000) {
        return { isValid: false, sanitized: '', error: 'Input too long (max 1000 characters)' };
    }

    return { isValid: true, sanitized, error: null };
};

// Formats timestamp for display
export const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    try {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
        return '';
    }
};

export const getCategoryFromEventType = (eventType) => {
    if (!eventType) return 'Work';

    const eventTypeLower = eventType.toLowerCase();

    if (eventTypeLower.includes('meeting') ||
        eventTypeLower.includes('interview') ||
        eventTypeLower.includes('team building') ||
        eventTypeLower.includes('training') ||
        eventTypeLower.includes('conference') ||
        eventTypeLower.includes('boardroom')) {
        return 'Meet';
    }

    if (eventTypeLower.includes('work') ||
        eventTypeLower.includes('individual') ||
        eventTypeLower.includes('private office') ||
        eventTypeLower.includes('virtual office') ||
        eventTypeLower.includes('hot desk') ||
        eventTypeLower.includes('dedicated desk')) {
        return 'Work';
    }

    if (eventTypeLower.includes('photo') ||
        eventTypeLower.includes('video') ||
        eventTypeLower.includes('shoot') ||
        eventTypeLower.includes('studio')) {
        return 'Shoot';
    }

    return 'Work';
};
